import { useState, useCallback, useEffect, useRef } from 'react'
import { parseIntervalToDays } from '~/common/utils/date-utils'

const WEEK_CONTAINER_WIDTH = 32
const PIXELS_PER_DAY = WEEK_CONTAINER_WIDTH / 7  // ~4.57px

/**
 * Custom hook for panning an entire crop schedule as a rigid body.
 *
 * When the user clicks and drags any phase rect, all phases shift by the
 * same number of days, preserving their durations and relative positions.
 * Tasks are also shifted by the same deltaDays to stay in sync.
 *
 * @param {Object} options
 * @param {Array} options.phases - Array of phase objects from schedule data
 * @param {Function} options.onPhaseAdjust - Callback with updated phases array
 * @param {Array} options.tasks - Array of task objects from schedule data
 * @param {Function} options.onTaskAdjust - Callback with updated tasks array
 * @returns {Object} { isPanning, handlePanStart }
 */
export const useSchedulePan = ({ phases, onPhaseAdjust, tasks, onTaskAdjust }) => {
  const [panState, setPanState] = useState({
    isPanning: false,
    startX: 0,
    startPhases: null,
    startTasks: null,
  })

  // Store callbacks in refs to avoid stale closures
  const phasesRef = useRef(phases)
  const tasksRef = useRef(tasks)
  const onPhaseAdjustRef = useRef(onPhaseAdjust)
  const onTaskAdjustRef = useRef(onTaskAdjust)
  const panStateRef = useRef(panState)

  useEffect(() => {
    phasesRef.current = phases
    tasksRef.current = tasks
    onPhaseAdjustRef.current = onPhaseAdjust
    onTaskAdjustRef.current = onTaskAdjust
    panStateRef.current = panState
  }, [phases, tasks, onPhaseAdjust, onTaskAdjust, panState])

  const handlePanStart = useCallback((clientX) => {
    const currentPhases = phasesRef.current
    if (!currentPhases || currentPhases.length === 0) return

    const currentTasks = tasksRef.current

    setPanState({
      isPanning: true,
      startX: clientX,
      startPhases: currentPhases.map(p => ({
        ...p,
        _startDuration: parseIntervalToDays(p.est_duration),
      })),
      startTasks: currentTasks?.map(t => ({ ...t })) ?? null,
    })
  }, [])

  const handlePanMove = useCallback((e) => {
    const state = panStateRef.current
    if (!state.isPanning || state.startPhases === null) return

    const deltaX = e.clientX - state.startX
    const deltaDays = Math.round(deltaX / PIXELS_PER_DAY)

    if (deltaDays === 0) return

    // Shift all phases by the same deltaDays, preserving durations
    const updatedPhases = state.startPhases.map(({ _startDuration, ...phase }) => {
      const originalStart = new Date(phase.est_started_at)
      const newStart = new Date(originalStart)
      newStart.setDate(newStart.getDate() + deltaDays)
      return {
        ...phase,
        est_started_at: newStart.toISOString(),
        // est_duration unchanged — rigid body pan
      }
    })

    if (onPhaseAdjustRef.current) {
      onPhaseAdjustRef.current(updatedPhases)
    }

    // Shift all tasks by the same deltaDays so they move in sync with phases
    if (state.startTasks && onTaskAdjustRef.current) {
      const updatedTasks = state.startTasks.map(task => {
        const originalStart = new Date(task.task_window_start)
        const newStart = new Date(originalStart)
        newStart.setDate(newStart.getDate() + deltaDays)
        return { ...task, task_window_start: newStart.toISOString() }
      })
      onTaskAdjustRef.current(updatedTasks)
    }
  }, [])

  const handlePanEnd = useCallback(() => {
    setPanState({
      isPanning: false,
      startX: 0,
      startPhases: null,
      startTasks: null,
    })
  }, [])

  // Attach/detach global mouse listeners
  useEffect(() => {
    if (panState.isPanning) {
      document.addEventListener('mousemove', handlePanMove)
      document.addEventListener('mouseup', handlePanEnd)

      return () => {
        document.removeEventListener('mousemove', handlePanMove)
        document.removeEventListener('mouseup', handlePanEnd)
      }
    }
  }, [panState.isPanning, handlePanMove, handlePanEnd])

  return {
    isPanning: panState.isPanning,
    handlePanStart,
  }
}
